/**
 * Production-ready logging utility
 *
 * In development: Logs to console
 * In production: Can be configured to send to error tracking service (Sentry, LogRocket, etc.)
 */

import { getRuntimeEnv } from "@/lib/runtimeEnv";

const IS_PRODUCTION = import.meta.env.PROD;
const IS_DEBUG = getRuntimeEnv("VITE_DEBUG") === "true";

interface LogContext {
  [key: string]: unknown;
}

class Logger {
  /**
   * Log an error with optional context
   */
  error(message: string, error?: Error | unknown, context?: LogContext) {
    if (!IS_PRODUCTION || IS_DEBUG) {
      console.error(message, error, context);
    }

    // TODO: In production, send to error tracking service
    // if (IS_PRODUCTION && window.Sentry) {
    //   window.Sentry.captureException(error, {
    //     tags: { ...context },
    //     extra: { message },
    //   });
    // }
  }

  /**
   * Log a warning with optional context
   */
  warn(message: string, context?: LogContext) {
    if (!IS_PRODUCTION || IS_DEBUG) {
      console.warn(message, context);
    }

    // TODO: Send warnings to tracking service if needed
  }

  /**
   * Log info (development only)
   */
  info(message: string, context?: LogContext) {
    if (!IS_PRODUCTION || IS_DEBUG) {
      console.info(message, context);
    }
  }

  /**
   * Log debug information (development only)
   */
  debug(message: string, context?: LogContext) {
    if (IS_DEBUG) {
      console.debug(message, context);
    }
  }

  /**
   * Log a caught error with user-friendly context
   */
  caught(error: Error | unknown, userMessage: string, context?: LogContext) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    const errorStack = error instanceof Error ? error.stack : undefined;

    this.error(`${userMessage}: ${errorMessage}`, error, {
      ...context,
      stack: errorStack,
      userMessage,
    });
  }
}

// Export singleton instance
export const logger = new Logger();

// Export for testing
export { Logger };
